import { useEffect, useState, type FormEvent } from "react";
import {
  Alert,
  Button,
  Checkbox,
  InputText,
  Select,
  Typography,
  type SelectOption,
} from "@/deps/ui-library";
import { BILLING_CONTACT } from "@/lib/billingContact";
import { SALES_COUNTRIES, salesCountry } from "@/lib/countries";

export const SIGNUP_PATH = "/signup";

const COUNTRY_OPTIONS: SelectOption[] = SALES_COUNTRIES.map((c) => ({
  value: c.code,
  label: c.name,
}));

interface Problem {
  title?: string;
  detail?: string;
  code?: string;
  errors?: Record<string, string[]>;
}

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
}

async function problemOf(response: Response): Promise<Problem> {
  try {
    return (await response.json()) as Problem;
  } catch {
    return {};
  }
}

/** Whether sign-up is open on this deployment, for the sign-in page's link to it. */
export function useSignupOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch("/bff/signup/status", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? (r.json() as Promise<{ enabled: boolean }>) : { enabled: false }))
      .then((status) => !cancelled && setOpen(status.enabled))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return open;
}

/**
 * Signing up for the 14-day free trial (suite ADR-0096). Two steps: the details, then a code from the email.
 * The company is made only when the code is right, and the person lands in the setup wizard, signed in.
 *
 * The password stays in this page's memory between the two steps and goes to the API once, with the code.
 */
export function SignupPage() {
  const [step, setStep] = useState<"details" | "code">("details");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  // The website's pricing passes the country it priced for (plan slice 10.10): /signup?country=US.
  const [country, setCountry] = useState(() => {
    const asked = new URLSearchParams(window.location.search).get("country")?.toUpperCase();
    return SALES_COUNTRIES.some((c) => c.code === asked) ? asked! : "NP";
  });
  const [taxId, setTaxId] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [trialUsed, setTrialUsed] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const chosen = salesCountry(country);
  const details = {
    email,
    fullName,
    companyName: company,
    countryCode: country,
    taxId: taxId || null,
  };

  const refuse = async (response: Response, fallback: string) => {
    const problem = await problemOf(response);
    setTrialUsed(problem.code === "trial_used");
    setFields(
      Object.fromEntries(
        Object.entries(problem.errors ?? {}).map(([key, messages]) => [key, messages[0] ?? ""]),
      ),
    );
    setError(problem.errors ? (problem.title ?? fallback) : (problem.title ?? fallback));
  };

  async function askForCode(event?: FormEvent) {
    event?.preventDefault();
    if (!agreed) {
      setError("Tick the box to agree to the terms.");
      return;
    }
    if (password.length < 8) {
      setFields({ password: "At least 8 characters." });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      const response = await post("/bff/signup/code", details);
      if (!response.ok) {
        await refuse(response, "That couldn't be sent. Try again shortly.");
        return;
      }
      setChallenge(((await response.json()) as { challenge: string }).challenge);
      setStep("code");
      setResendIn(30);
    } finally {
      setBusy(false);
    }
  }

  async function confirm(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await post("/bff/signup", {
        ...details,
        challenge,
        code: code.trim(),
        password,
        mobileNumber: mobile || null,
      });
      if (!response.ok) {
        await refuse(response, "That didn't work. Try again.");
        return;
      }
      setPassword("");
      // A full load, so the session is read afresh: the person is signed in, in their new company, and chooses
      // between setting it up and exploring the demo company first.
      window.location.assign("/start");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="bg-canvas grid min-h-screen lg:grid-cols-[480px_1fr]">
      <div className="bg-card flex flex-col justify-center gap-5 px-8 py-10">
        <div className="flex flex-col gap-1">
          <h1 className="text-fg m-0 text-xl font-semibold">
            {step === "details" ? "Create your Rigo account" : "Confirm your email"}
          </h1>
          <Typography.Text as="p" variant="small" muted className="m-0">
            {step === "details"
              ? "14 days of Pro with the Reporting layer. No card required."
              : `We sent a 6-digit code to ${email}. Enter it to start your trial.`}
          </Typography.Text>
        </div>

        {error ? (
          <Alert
            config={{
              variant: "subtle",
              colorScheme: trialUsed ? "warning" : "danger",
              size: "sm",
            }}
            description={
              trialUsed
                ? `${error} Each company gets one trial. To subscribe, write to ${BILLING_CONTACT.email}.`
                : error
            }
          />
        ) : null}

        {step === "details" ? (
          <form onSubmit={(e) => void askForCode(e)} className="flex flex-col gap-3">
            <InputText
              label="Full name"
              autoComplete="name"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              errorText={fields.fullName}
            />
            <InputText
              label="Work email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              errorText={fields.email}
            />
            <InputText
              label="Mobile number"
              type="tel"
              autoComplete="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
            />
            <InputText
              label="Password"
              type="password"
              autoComplete="new-password"
              required
              helperText="At least 8 characters."
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              errorText={fields.password}
            />
            <InputText
              label="Company name"
              autoComplete="organization"
              required
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              errorText={fields.companyName}
            />
            <Select
              label="Country"
              options={COUNTRY_OPTIONS}
              value={country}
              showClearButton={false}
              onChange={(details) => {
                const picked = Array.isArray(details.value) ? details.value[0] : details.value;
                if (picked) {
                  setCountry(String(picked.value));
                  setTaxId("");
                }
              }}
            />
            <InputText
              label={chosen.idLabel}
              inputMode={chosen.code === "AE" ? "text" : "numeric"}
              required={chosen.required}
              helperText={chosen.idHint || undefined}
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              errorText={fields.taxId}
            />
            <Checkbox
              label="I've read and agree to the terms of use, privacy notice and offer details."
              checked={agreed}
              onCheckedChange={(details) => setAgreed(details.checked === true)}
            />
            <Button colorScheme="primary" type="submit" disabled={busy}>
              {busy ? "Sending the code" : "Start 14-day free trial"}
            </Button>
            <Typography.Text as="p" variant="small" muted className="m-0">
              When the trial ends the books lock until you subscribe. An administrator can still
              export them for 90 days.
            </Typography.Text>
          </form>
        ) : (
          <form onSubmit={(e) => void confirm(e)} className="flex flex-col gap-3">
            <InputText
              label="Verification code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button colorScheme="primary" type="submit" disabled={busy || code.trim().length !== 6}>
              {busy ? "Setting up your company" : "Confirm and continue"}
            </Button>
            <Button
              type="button"
              variant="link"
              colorScheme="primary"
              size="sm"
              className="self-start"
              disabled={resendIn > 0 || busy}
              onClick={() => void askForCode()}
            >
              {resendIn > 0 ? `Send a new code in ${resendIn}s` : "Send a new code"}
            </Button>
            <Button
              type="button"
              variant="link"
              colorScheme="default"
              size="sm"
              className="self-start"
              onClick={() => {
                setStep("details");
                setCode("");
                setError(null);
              }}
            >
              Change the details
            </Button>
          </form>
        )}

        <Typography.Text as="p" variant="small" muted className="m-0">
          Already have an account?{" "}
          <a href="/" className="text-primary font-medium no-underline hover:underline">
            Sign in
          </a>
        </Typography.Text>
      </div>
      <div className="bg-muted hidden flex-col items-center justify-center gap-2 p-10 lg:flex">
        <Typography.Text as="p" className="m-0 text-lg font-semibold">
          Rigo Accounting
        </Typography.Text>
        <Typography.Text as="p" variant="small" muted className="m-0 max-w-sm text-center">
          Double-entry books with approvals and a full audit trail. A country pack adds the local
          tax codes, fiscal year and statutory reports.
        </Typography.Text>
      </div>
    </main>
  );
}
